import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Send } from "lucide-react";
import api from "@/lib/api";

function relativeDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const min = Math.round(diffMs / 60000);
    if (min < 1) return "à l'instant";
    if (min < 60) return `il y a ${min} min`;
    const h = Math.round(min / 60);
    if (h < 24) return `il y a ${h} h`;
    const days = Math.round(h / 24);
    if (days < 30) return `il y a ${days} j`;
    return d.toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" });
  } catch { return iso.slice(0, 10); }
}

/**
 * Comments — public comment thread for Le Carnet posts.
 * No anonymous : name + email required (email is never displayed).
 */
export default function Comments({ slug }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", content: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api.get(`/blog/${slug}/comments`)
      .then((r) => setComments(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.content.trim()) {
      return toast.error("Nom, email et commentaire sont requis.");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/blog/${slug}/comments`, form);
      setComments((prev) => [{ ...data }, ...prev]);
      setForm({ name: "", email: "", content: "" });
      toast.success("Merci — votre commentaire est ajouté.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-24 md:mt-32 border-t border-fg/15 pt-14 md:pt-20 max-w-[720px]" data-testid="carnet-comments">
      <div className="meta-label text-cinabre flex items-center gap-2">
        <MessageSquare size={13} strokeWidth={1.5} />
        § Discussion · {comments.length} commentaire{comments.length > 1 ? "s" : ""}
      </div>
      <h2 className="mt-4 font-serif font-light text-4xl md:text-5xl leading-[0.98] tracking-tight">
        Laisser une <em className="italic">trace</em>.
      </h2>
      <p className="mt-6 font-serif text-xl md:text-2xl leading-[1.5] text-fg/90 max-w-2xl">
        <strong className="text-fg">Votre voix compte.</strong> Un mot, une réaction, une histoire qui vous vient. <em className="italic text-fg/70">Nom et email requis — l'email reste privé.</em>
      </p>

      {/* Form */}
      <form onSubmit={submit} className="mt-10 space-y-5 border border-fg/15 p-6 md:p-8 bg-fg/[0.02]" data-testid="comment-form">
        <div className="grid md:grid-cols-2 gap-5">
          <label className="block">
            <span className="meta-label text-fg/60">Votre nom *</span>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Prénom & Nom"
              className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 font-serif text-lg focus:outline-none focus:border-cinabre"
              data-testid="comment-name"
              maxLength={80}
              required
            />
          </label>
          <label className="block">
            <span className="meta-label text-fg/60">Votre email * <span className="text-fg/40">(privé)</span></span>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="vous@exemple.fr"
              className="mt-2 w-full border-b border-fg/30 bg-transparent py-2 font-mono text-sm focus:outline-none focus:border-cinabre"
              data-testid="comment-email"
              maxLength={200}
              required
            />
          </label>
        </div>
        <label className="block">
          <span className="meta-label text-fg/60">Votre commentaire *</span>
          <textarea
            rows={4}
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            placeholder="Un mot, une histoire, une réaction…"
            className="mt-2 w-full border border-fg/20 bg-transparent p-3 font-serif text-base leading-relaxed focus:outline-none focus:border-cinabre resize-vertical"
            data-testid="comment-content"
            maxLength={2000}
            required
          />
        </label>
        <div className="flex items-center justify-between gap-4 border-t border-fg/10 pt-4 flex-wrap">
          <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">
            § Les commentaires anonymes ne sont pas acceptés.
          </span>
          <button type="submit" disabled={submitting} className="btn-editorial disabled:opacity-40 disabled:cursor-not-allowed" data-testid="comment-submit">
            {submitting ? "Envoi…" : "Publier"} <Send size={14} />
          </button>
        </div>
      </form>

      {/* Comments list */}
      <div className="mt-12 space-y-8" data-testid="comment-list">
        {loading && (
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50 animate-pulse">
            Chargement des voix…
          </div>
        )}
        {!loading && comments.length === 0 && (
          <div className="border border-dashed border-fg/25 p-8 text-center">
            <p className="font-serif italic text-lg text-fg/60">
              Personne n'a encore laissé de trace. Soyez la première voix.
            </p>
          </div>
        )}
        {comments.map((c) => (
          <article key={c.id} className="border-l-2 border-cinabre pl-6 py-1" data-testid={`comment-${c.id}`}>
            <header className="flex items-baseline gap-3 flex-wrap">
              <div className="font-serif text-xl leading-tight">{c.name}</div>
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/55">
                · {relativeDate(c.created_at)}
              </div>
            </header>
            <p className="mt-3 font-serif text-base md:text-lg leading-[1.6] text-fg/85 whitespace-pre-wrap">
              {c.content}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}
